import type { Comment, CommentListOrder, CommentTargetType } from '@/@types/comment'
import type { ContentType } from '@/@types/common'
import { GET_COMMENT, LIST_COMMENTS, LIST_PRIMARY_COMMENTS } from '@/constants/api'
import type {
	InfiniteData,
	UseInfiniteQueryOptions,
	UseSuspenseQueryOptions,
} from '@tanstack/react-query'
import type { AxiosRequestConfig } from 'axios'
import { pick } from 'lodash-es'
import queryClient from '../queryClient'
import { getAxiosInstance } from '../request'

/**
 * ***************** Comment 👇 *****************
 */

export interface GetCommentParams {
	id: string
	targetType: ContentType
}

export function generateCommentQueryKey(params: GetCommentParams) {
	return [GET_COMMENT, params]
}

const cacheCommentThread = (comment: Comment) => {
	queryClient.setQueryData(generateCommentQueryKey(pick(comment, ['id', 'targetType'])), comment)

	for (const reply of comment.hotReplies ?? []) {
		cacheCommentThread(reply)
	}
}

export async function getComment(
	params: GetCommentParams,
	config?: Pick<AxiosRequestConfig, 'suppressToast'>
): Promise<Comment> {
	const comment = await getAxiosInstance()
		.get(GET_COMMENT, {
			...config,
			params,
		})
		.then((res) => res.data.data)

	for (const reply of comment.hotReplies ?? []) {
		cacheCommentThread(reply)
	}

	return comment
}

export function getCommentQueryOptions(params: GetCommentParams): UseSuspenseQueryOptions<Comment> {
	return {
		queryKey: generateCommentQueryKey(params),
		queryFn: () => getComment(params),
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}

/**
 * ***************** Comment List 👇 *****************
 */

export interface QueryCommentsParams {
	targetId: string
	targetType: CommentTargetType
	primaryCommentId?: string
	level?: number
	limit?: number
	order?: CommentListOrder
}

interface QueryCommentsPage {
	data: Comment[]
	loadMoreKey?: unknown
}

const isCommentThreadQuery = (
	queryKey: readonly unknown[],
	comment: Pick<Comment, 'targetId' | 'targetType'>,
	primaryCommentId: string
) => {
	const params = queryKey[1] as QueryCommentsParams | undefined

	return (
		queryKey[0] === LIST_COMMENTS &&
		params?.targetId === comment.targetId &&
		params.targetType === comment.targetType &&
		params.primaryCommentId === primaryCommentId
	)
}

export const addCommentReplyToCache = (parentComment: Comment, reply: Comment) => {
	const primaryCommentId = parentComment.level <= 1 ? parentComment.id : parentComment.threadId
	const primaryCommentQueryKey = generateCommentQueryKey({
		id: primaryCommentId,
		targetType: parentComment.targetType,
	})

	cacheCommentThread(reply)
	queryClient.setQueryData<Comment>(primaryCommentQueryKey, (primaryComment) => {
		if (!primaryComment) return primaryComment

		const hotReplies = primaryComment.hotReplies ?? []
		if (hotReplies.some((item) => item.id === reply.id)) return primaryComment

		return {
			...primaryComment,
			replyCount: primaryComment.replyCount + 1,
			hotReplies: [reply, ...hotReplies],
		}
	})
	void queryClient.invalidateQueries({
		predicate: (query) => isCommentThreadQuery(query.queryKey, parentComment, primaryCommentId),
	})
}

export const removeCommentReplyFromCache = (comment: Comment) => {
	const primaryCommentId = comment.threadId
	const primaryCommentQueryKey = generateCommentQueryKey({
		id: primaryCommentId,
		targetType: comment.targetType,
	})

	queryClient.setQueryData<Comment>(primaryCommentQueryKey, (primaryComment) =>
		primaryComment
			? {
					...primaryComment,
					replyCount: Math.max(0, primaryComment.replyCount - 1),
					hotReplies: primaryComment.hotReplies?.filter((item) => item.id !== comment.id),
				}
			: primaryComment
	)
	queryClient.setQueriesData<InfiniteData<QueryCommentsPage>>(
		{
			predicate: (query) => isCommentThreadQuery(query.queryKey, comment, primaryCommentId),
		},
		(data) =>
			data
				? {
						...data,
						pages: data.pages.map((page) => ({
							...page,
							data: page.data.filter((item) => item.id !== comment.id),
						})),
					}
				: data
	)
}

export function generatePrimaryCommentsQueryKey(params: QueryCommentsParams) {
	return [LIST_PRIMARY_COMMENTS, params]
}

export function generateNonPrimaryCommentsQueryKey(params: QueryCommentsParams) {
	return [LIST_COMMENTS, params]
}

export function getPrimaryCommentsQueryOptions(
	params: QueryCommentsParams
): UseInfiniteQueryOptions<QueryCommentsPage, unknown, InfiniteData<QueryCommentsPage>> {
	return {
		queryKey: generatePrimaryCommentsQueryKey(params),
		queryFn: ({ pageParam }) =>
			getAxiosInstance()
				.post<QueryCommentsPage>(LIST_PRIMARY_COMMENTS, {
					limit: 20,
					loadMoreKey: pageParam,
					...params,
				})
				.then((res) => {
					const body = res.data

					for (const item of body.data) {
						cacheCommentThread(item)
					}

					return body
				}),
		getNextPageParam: (lastPage: QueryCommentsPage) => lastPage.loadMoreKey,
		initialPageParam: undefined,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}

export function getNonPrimaryCommentsQueryOptions(
	params: QueryCommentsParams
): UseInfiniteQueryOptions<QueryCommentsPage, unknown, InfiniteData<QueryCommentsPage>> {
	return {
		queryKey: generateNonPrimaryCommentsQueryKey(params),
		queryFn: ({ pageParam }) =>
			getAxiosInstance()
				.post<QueryCommentsPage>(LIST_COMMENTS, {
					limit: 20,
					loadMoreKey: pageParam,
					...params,
				})
				.then((res) => {
					const body = res.data

					for (const item of body.data) {
						cacheCommentThread(item)
					}

					return body
				}),
		getNextPageParam: (lastPage: QueryCommentsPage) => lastPage.loadMoreKey,
		initialPageParam: undefined,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}
